import {
  LeadFormVisibilityMode,
  type ProfileTemplateFieldMode,
  type ProfileTemplateFieldPoliciesDto,
} from '@up2stay/up2promo-api-client';
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  cn,
  ghostAccentIconTileClass,
} from '@up2stay/up2promo-ui';
import { MessageSquare, Minus, Plus, Share2 } from 'lucide-react';

import { TemplateFieldRow } from '@/core/components/profile-templates/template-field-row';
import { TemplatePolicyMemberControls } from '@/core/components/profile-templates/template-policy-member-controls';
import type { TemplateMemberValuesEditorUiProps } from '@/core/components/profile-templates/template-policy-member-controls';
import {
  TEMPLATE_DETAIL_MUTED_CLASS,
  TEMPLATE_DETAIL_SECTION_TITLE_CLASS,
  TemplateDetailSectionCard,
} from '@/core/components/profile-templates/template-detail-section-card';
import type { ProfileTemplateFieldKey } from '@/core/lib/profile-template-fields';
import type { TemplateFormData } from '@/core/lib/profile-template-form';

export type TemplateDetailLeadFormDict = {
  title: string;
  description: string;
  enabled: string;
  enabledDescription: string;
  visibilityModeLabel: string;
  visibilityMode: {
    manual: string;
    autoOpen: string;
  };
  autoOpenDelayLabel: string;
};

export type TemplateDetailLeadsWorkspaceDict = {
  title: string;
  description: string;
  toggleLabel: string;
};

type TemplateDetailLeadFormSectionProps = {
  leadFormDict: TemplateDetailLeadFormDict;
  leadsWorkspaceDict: TemplateDetailLeadsWorkspaceDict;
  leadsAutoShareFieldLabel: string;
  policies: ProfileTemplateFieldPoliciesDto;
  onPolicyChange: (
    key: ProfileTemplateFieldKey,
    mode: ProfileTemplateFieldMode,
  ) => void;
  canEditTemplate: boolean;
  policyLabels: { locked: string; free: string };
  memberValueProps: TemplateMemberValuesEditorUiProps;
  watchedForm: TemplateFormData;
  disabled: boolean;
  showLeadsWorkspaceShare: boolean;
  onFieldChange: <K extends keyof TemplateFormData>(
    key: K,
    value: TemplateFormData[K],
  ) => void;
};

export function TemplateDetailLeadFormSection({
  leadFormDict,
  leadsWorkspaceDict,
  leadsAutoShareFieldLabel,
  policies,
  onPolicyChange,
  canEditTemplate,
  policyLabels,
  memberValueProps,
  watchedForm,
  disabled,
  showLeadsWorkspaceShare,
  onFieldChange,
}: TemplateDetailLeadFormSectionProps) {
  return (
    <div className="flex w-full min-w-0 flex-col gap-4">
      <TemplateDetailSectionCard contentClassName="gap-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 flex-col gap-2">
            <div className="flex items-center gap-2.5">
              <div
                className={cn(
                  'flex size-8 shrink-0 items-center justify-center rounded-[var(--r-control)]',
                  ghostAccentIconTileClass,
                )}
                aria-hidden
              >
                <MessageSquare className="size-4" />
              </div>
              <h2 className={TEMPLATE_DETAIL_SECTION_TITLE_CLASS}>
                {leadFormDict.title}
              </h2>
            </div>
            <p className={TEMPLATE_DETAIL_MUTED_CLASS}>
              {leadFormDict.description}
            </p>
          </div>
          {canEditTemplate ? (
            <TemplatePolicyMemberControls
              value={policies.leadForm}
              onChange={(mode) => onPolicyChange('leadForm', mode)}
              labels={policyLabels}
            />
          ) : null}
        </div>

        <div className="flex min-w-0 flex-col gap-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <Label htmlFor="leadFormEnabled">{leadFormDict.enabled}</Label>
              <p className={TEMPLATE_DETAIL_MUTED_CLASS}>
                {leadFormDict.enabledDescription}
              </p>
            </div>
            <Switch
              id="leadFormEnabled"
              checked={watchedForm.leadFormEnabled ?? false}
              disabled={disabled}
              onCheckedChange={(checked) => {
                onFieldChange('leadFormEnabled', !!checked);
              }}
            />
          </div>

          {watchedForm.leadFormEnabled ? (
            <>
              <div className="flex min-w-0 flex-col gap-1.5">
                <Label htmlFor="leadFormVisibilityMode">
                  {leadFormDict.visibilityModeLabel}
                </Label>
                <Select
                  value={
                    watchedForm.leadFormVisibilityMode === 'off'
                      ? 'manual'
                      : (watchedForm.leadFormVisibilityMode ?? 'manual')
                  }
                  disabled={disabled}
                  onValueChange={(value) => {
                    const nextMode = value as 'manual' | 'auto_open';
                    onFieldChange('leadFormVisibilityMode', nextMode);
                  }}
                >
                  <SelectTrigger
                    id="leadFormVisibilityMode"
                    className="min-w-0 w-full"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={LeadFormVisibilityMode.MANUAL}>
                      {leadFormDict.visibilityMode.manual}
                    </SelectItem>
                    <SelectItem value={LeadFormVisibilityMode.AUTO_OPEN}>
                      {leadFormDict.visibilityMode.autoOpen}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {watchedForm.leadFormVisibilityMode === 'auto_open' ? (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="leadFormAutoOpenDelay">
                    {leadFormDict.autoOpenDelayLabel}
                  </Label>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-10 w-10 shrink-0"
                      disabled={
                        disabled ||
                        (watchedForm.leadFormAutoOpenDelay ?? 0) <= 0
                      }
                      onClick={() => {
                        const current = watchedForm.leadFormAutoOpenDelay ?? 0;
                        onFieldChange(
                          'leadFormAutoOpenDelay',
                          Math.max(0, current - 1),
                        );
                      }}
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <Input
                      id="leadFormAutoOpenDelay"
                      type="number"
                      min={0}
                      max={15}
                      value={watchedForm.leadFormAutoOpenDelay ?? 0}
                      readOnly
                      className="w-20 text-center"
                      disabled={disabled}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-10 w-10 shrink-0"
                      disabled={
                        disabled ||
                        (watchedForm.leadFormAutoOpenDelay ?? 0) >= 15
                      }
                      onClick={() => {
                        const current = watchedForm.leadFormAutoOpenDelay ?? 0;
                        onFieldChange(
                          'leadFormAutoOpenDelay',
                          Math.min(15, current + 1),
                        );
                      }}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </TemplateDetailSectionCard>

      {showLeadsWorkspaceShare ? (
        <TemplateDetailSectionCard contentClassName="gap-4">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2.5">
              <div
                className={cn(
                  'flex size-8 shrink-0 items-center justify-center rounded-[var(--r-control)]',
                  ghostAccentIconTileClass,
                )}
                aria-hidden
              >
                <Share2 className="size-4" />
              </div>
              <h2 className={TEMPLATE_DETAIL_SECTION_TITLE_CLASS}>
                {leadsWorkspaceDict.title}
              </h2>
            </div>
            <p className={TEMPLATE_DETAIL_MUTED_CLASS}>
              {leadsWorkspaceDict.description}
            </p>
          </div>
          <TemplateFieldRow
            fieldKey="leadsAutoShareWithWorkspace"
            policies={policies}
            onPolicyChange={onPolicyChange}
            canManage={canEditTemplate}
            policyLabels={policyLabels}
            memberValueColumn={{
              kind: 'setting',
              key: 'leadsAutoShareWithWorkspace',
              label: leadsAutoShareFieldLabel,
              inputType: 'boolean',
            }}
            memberDialogTitle={leadsWorkspaceDict.toggleLabel}
            memberDefaultValue={
              (watchedForm.leadsAutoShareWithWorkspace ?? false)
                ? 'true'
                : 'false'
            }
            {...memberValueProps}
          >
            <div className="flex items-center justify-between gap-4">
              <Label
                htmlFor="leadsAutoShareWithWorkspace"
                className="min-w-0 flex-1 cursor-pointer text-[13.5px] font-normal leading-snug"
              >
                {leadsWorkspaceDict.toggleLabel}
              </Label>
              <Switch
                id="leadsAutoShareWithWorkspace"
                checked={watchedForm.leadsAutoShareWithWorkspace ?? false}
                disabled={disabled}
                onCheckedChange={(checked) => {
                  onFieldChange('leadsAutoShareWithWorkspace', checked);
                }}
              />
            </div>
          </TemplateFieldRow>
        </TemplateDetailSectionCard>
      ) : null}
    </div>
  );
}
